'use client';

import { Button } from '@jouzen/ecom-components/v3/Button';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { box } from '@/app/styles/ui';
import type { LineItem, MessageKey, Order, PartnerConfig } from '@/types';
import { OrderStatus } from '@/types/Order';

import OrderInfo, { type OrderInfoProps } from '../OrderInfo';
import OrderItems, { OrderLineItem } from '../OrderItems';

export interface OrderDisplayProps {
  readonly className?: string;
  readonly isEnrolled?: boolean;
  readonly isOpen?: boolean;
  readonly lineItems: LineItem[];
  readonly orderId: Order['netsuiteOrderId'];
  readonly shippingAddress: OrderInfoProps['address'];
  readonly orderDate: Order['orderDate'];
  readonly onSelectOrder: (order: Order) => void;
  readonly onOpen: () => void;
  readonly cobrandedConfig?: PartnerConfig;
  readonly status: Order['orderStatus'];
}

const OrderDisplay = ({
  className,
  isEnrolled,
  isOpen,
  lineItems,
  orderDate,
  orderId,
  shippingAddress,
  cobrandedConfig,
  onSelectOrder,
  onOpen,
  status,
}: OrderDisplayProps): JSX.Element | null => {
  const t = useTranslations();
  const isSingleRing = lineItems.length === 1;
  const isComplete = status === OrderStatus.completed;

  const actionButtonText: { text: MessageKey; testid: string } = useMemo(() => {
    if (!isEnrolled) {
      return {
        text: 'eop_start_order_button',
        testid: 'order-display-start-order-button',
      };
    }
    if (isSingleRing) {
      return {
        text: 'eop_select_ring_button',
        testid: 'order-display-select-ring-button',
      };
    }
    return {
      text: 'eop_select_rings_button',
      testid: 'order-display-select-rings-button',
    };
  }, [isEnrolled, isSingleRing]);

  const statusText: MessageKey = useMemo(() => {
    switch (status) {
      case OrderStatus.completed:
        return 'eop_home_orders_page_status_completed';
      case OrderStatus.closed:
        return 'eop_home_orders_page_status_closed';
      case OrderStatus.expired:
      case OrderStatus.ineligible:
        return 'eop_home_orders_page_status_expired';
      default:
        return 'eop_home_orders_page_status_pending';
    }
  }, [status]);

  return (
    <div
      data-testid="order-display"
      className={cx(box({ p: 10 }), 'flex flex-col gap-10', className)}
    >
      <div className="flex items-center justify-between">
        <Typography
          data-testid="order-display-status"
          className="rounded bg-black px-3 py-1 uppercase tracking-wider"
          color="light"
          variant="eyebrow-small"
          weight="bold"
        >
          {t(statusText)}
        </Typography>

        {!isComplete ? (
          <Button
            onClick={onSelectOrder}
            data-testid={actionButtonText.testid}
            disabled={!orderId || status !== OrderStatus.pending}
          >
            {t(actionButtonText.text)}
          </Button>
        ) : null}

        {isComplete && !isSingleRing ? (
          <Button
            onClick={onOpen}
            data-testid="order-display-view-order-button"
            variant="outlined-dark"
          >
            {isOpen ? t('eop_hide_order_button') : t('eop_view_order_button')}
          </Button>
        ) : null}
      </div>

      {isComplete && isSingleRing && <OrderLineItem lineItem={lineItems[0]} />}

      <div className={cx(box({ bg: 'gray' }), 'w-full grid gap-4')}>
        <OrderInfo
          className="lg:col-span-2 lg:row-start-1"
          address={shippingAddress}
          cobrandedConfig={cobrandedConfig}
          details={{
            number: orderId!,
            date: new Date(orderDate!),
            quantity: lineItems.length ?? 0,
          }}
        />
      </div>

      {isOpen && isComplete && Boolean(lineItems.length) && (
        <OrderItems lineItems={lineItems} />
      )}
    </div>
  );
};

export default OrderDisplay;
